import type { Project, Space } from "@/api/spaces";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import {
  isFrameHiddenByMetadata,
  selectFramesByType,
} from "@/stores/frames-selectors";
import { useMemoryStore } from "@/stores/memory-store";
import { useProjectStore } from "@/stores/project-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { translate } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { useNotificationStore } from "@/stores/notification-store";
import { isTauri } from "@/lib/is-tauri";
import { replaceFocusPresentationMode } from "@/lib/focus-presentation";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { ensureMemoryFrame } from "@/desktop/onboarding/workspace-frames/ensure-memory-frame";
import { setFrameHidden } from "@/lib/frame-visibility";
import { openFramesNow } from "@/lib/open-frames";
import { markWallItemOpened } from "@/lib/wall-new-opened";

interface OpenMemoryNodeOptions {
  /** Thing to focus; null only shows the memory frame. */
  nodeId: string | null;
  currentProject: Project | null | undefined;
  currentSpace: Space | null | undefined;
  selectFrame: (frameId: string) => void;
}

/** Shows the memory frame, creating it when the project has none; with a thing, focuses it and opens it in the pocket. */
export function openMemoryNode({
  nodeId,
  currentProject,
  currentSpace,
  selectFrame,
}: OpenMemoryNodeOptions): void {
  const authCurrent = captureDesktopChatAuthScopeGuard();
  const projectId = currentProject?.id ?? null;
  const spaceId = currentSpace?.id ?? null;
  const current = () => {
    const project = useProjectStore.getState();
    return (
      authCurrent() &&
      (project.currentProject?.id ?? null) === projectId &&
      (project.currentSpace?.id ?? null) === spaceId
    );
  };
  if (!current()) return;
  if (nodeId) {
    useMemoryStore.getState().openThing(nodeId, undefined, "jump");
    markWallItemOpened(`thing:${nodeId}`);
  }

  const showFrame = (frameId: string) => {
    if (!current()) return;
    const frame = useFramesStore.getState().framesById[frameId];
    if (!frame) return;
    // On the wall the Pinned row loads the memory frame; it opens in focus.
    if (openFramesNow([frameId], "navigate")) return;
    if (isFrameHiddenByMetadata(frame)) setFrameHidden(frameId, false);
    if (isTauri() && useDesktopMode.getState().mode !== "denk") {
      replaceFocusPresentationMode(frameId, [frameId]);
    } else if (
      usePresentationStore.getState().presentation?.variant === "focus"
    ) {
      activateFrameInCurrentContext(frameId);
    } else {
      selectFrame(frameId);
      requestAnimationFrame(() => {
        if (current()) useCanvasStore.getState().navigateToFrame(frameId);
      });
    }
  };
  const [memoryFrame] = selectFramesByType(useFramesStore.getState(), "memory");

  if (memoryFrame) {
    showFrame(memoryFrame.frameId);
    return;
  }

  if (!currentProject || !currentSpace) {
    useNotificationStore.getState().addSystemToast({
      id: "memory-open-error",
      title: translate(layoutMessages, "chooseProjectForMemory"),
      level: "error",
    });
    return;
  }

  const controller = new AbortController();
  const unsubscribeProject = useProjectStore.subscribe(() => {
    if (!current()) controller.abort();
  });
  const unsubscribeAuth = subscribeDesktopChatAuthScope(() =>
    controller.abort(),
  );
  ensureMemoryFrame(currentProject.id, controller.signal)
    .then(showFrame)
    .catch(() => {
      if (!current() || controller.signal.aborted) return;
      useNotificationStore.getState().addSystemToast({
        id: "memory-open-error",
        title: translate(layoutMessages, "memoryOpenFailed"),
        level: "error",
      });
    })
    .finally(() => {
      unsubscribeProject();
      unsubscribeAuth();
    });
}
